import React from 'react';
import tw from 'twin.macro';
import { StaticImage } from 'gatsby-plugin-image';

import Layout from '~components/Layout';
import SEO from '~components/SEO';

import Button from '~components/Button';
import Heading, { Heading1 } from '~components/Heading';
import Logo from '~components/Logo';
import Text from '~components/Text';
import FullscreenVideo from '~components/FullscreenVideo';

import HoldingHandsShield from '~components/Icon/HoldingHandsShield';
import ThreeDoctors from '~components/Icon/ThreeDoctors';
import TwoDoctors from '~components/Icon/TwoDoctors';
import HeartInHands from '~components/Icon/HeartInHands';
import PersonInHouseCircle from '~components/Icon/PersonInHouseCircle';
import SpeechBubblePlusCircle from '~components/Icon/SpeechBubblePlusCircle';
import FamilyScaleCircle from '~components/Icon/FamilyScaleCircle';
import PenPaperCircle from '~components/Icon/PenPaperCircle';
import MaskCircle from '~components/Icon/MaskCircle';

import { fsbgmedia } from '~theme';

import heroWebm from '../videos/hero.webm';
import heroMp4 from '../videos/hero.mp4';

import missionVisionWebm from '../videos/mission-vision-values.webm';
import missionVisionMp4 from '../videos/mission-vision-values.mp4';

import caringCommunityWebm from '../videos/our-community.webm';
import caringCommunityMp4 from '../videos/our-community.mp4';

import betterCareWebm from '../videos/better-care.webm';
import betterCareMp4 from '../videos/better-care.mp4';

import supporintgOurTeamWebm from '../videos/supporting-our-team.webm';
import supporintgOurTeamMp4 from '../videos/supporting-our-team.mp4';

import homeCareScienceWebm from '../videos/home-care-science.webm';
import homeCareScienceMp4 from '../videos/home-care-science.mp4';

import safePlaceWebm from '../videos/safe-place.webm';
import safePlaceMp4 from '../videos/safe-place.mp4';

const IndexPage: React.FC = () => (
  <Layout>
    <SEO />
    <FullscreenVideo
      src={{
        webm: heroWebm,
        mp4: heroMp4
      }}
    >
      <div tw="relative py-12">
        <div tw="relative z-20 px-6 md:px-12">
          <Logo tw="h-auto w-full mb-64 md:(h-48 w-auto mb-96)" />
          <Heading1 tw="text-white">Impact Report 2021</Heading1>
        </div>
      </div>
    </FullscreenVideo>
    <div tw="w-full relative pt-12">
      <div tw="ml-6 md:ml-12 relative z-20">
        <div tw="xl:(grid gap-12 grid-cols-3)">
          <div tw="mr-12 xl:(mr-0 col-span-1 pb-24)">
            <Heading level="h2">Messages from Our Leaders</Heading>
            <p tw="text-base md:text-xl my-4">
            Dr. Kathryn Nichol, President & CEO, Karen N. Singh, Board Chair, and Amr Elimam, Co-Chair of VHA's Client and Carer Advisory Council share their thoughts.{' '}
            </p>
            <p tw="my-6">
              <Button to="/message">View Messages from Our Leaders</Button>
            </p>
          </div>
          <div tw="flex items-end xl:(col-span-2 pt-48)">
            <StaticImage src="../images/leaders.png" alt="" css={tw`w-full h-auto`} />
          </div>
        </div>
      </div>
      <StaticImage src="../images/message-bg.jpg" alt="" css={fsbgmedia} />
    </div>
    <FullscreenVideo
      src={{
        webm: missionVisionWebm,
        mp4: missionVisionMp4
      }}
    >
      <div tw="bg-[#41020A] text-white bg-opacity-95 pt-12 xl:text-right">
        <div tw="grid xl:(grid-cols-2 gap-12)">
          <div tw="flex flex-col lg:(col-start-2 row-start-1)">
            <div tw="px-6 xl:px-12">
              <Heading tw="leading-tight my-0" level="h2">
                Vision, Mission &amp; Core Beliefs
              </Heading>
              <p tw="text-base md:text-xl my-6">
                In every role at every level, we lead with purpose, build bonds that matter and
                we’re passionate about providing spectacular care.{' '}
              </p>
              <p tw="my-4">
              <Button to="/vision-mission-core-beliefs">View our Vision, Mission & Core Beliefs</Button>
            </p>
            </div>
          </div>
          <div tw="lg:(col-start-1 row-start-1)">
            <StaticImage src="../images/arms-crossed-red-hero.png" alt="" />
          </div>
        </div>
      </div>
    </FullscreenVideo>
    <FullscreenVideo
      src={{
        webm: caringCommunityWebm,
        mp4: caringCommunityMp4
      }}
    >
      <div tw="bg-[#51006D] text-white bg-opacity-95 pt-12">
        <div tw="grid lg:(grid-cols-2 gap-12 items-end)">
          <div tw="flex flex-col items-center justify-center">
            <div tw="px-6 lg:px-12">
              <Heading tw="leading-tight my-0" level="h2">
                Caring For Our Community
              </Heading>
              <p tw="text-base md:text-xl my-4">
                From Personal Support Worker Rina Min’s first visit to her client Gwen Noble’s home,
                the pair felt like they were family.
              </p>
              <Text tw="mt-6">
                <Button to="/caring-community/">View Caring for our Community</Button>
              </Text>
              <div tw="flex items-center space-x-6 mb-12 mt-8">
                <div tw="flex items-center space-x-4">
                  <PersonInHouseCircle tw="w-20 h-20 md:(w-24 h-24) flex-shrink-0 lg:(w-48 h-48)" />
                  <p>
                    <span tw="block text-2xl md:text-4xl font-bold">113,199</span>
                    <span tw="block text-base md:text-xl">
                      Clients admitted to care where and when they need it
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <StaticImage src="../images/rena-hero.png" alt="" />
          </div>
        </div>
      </div>
    </FullscreenVideo>
    <FullscreenVideo
      src={{
        webm: betterCareWebm,
        mp4: betterCareMp4
      }}
    >
      <div tw="bg-[#064B00] text-white bg-opacity-95 pt-12 lg:text-right">
        <div tw="grid lg:(grid-cols-2 gap-12 items-end)">
          <div tw="flex flex-col items-center justify-center lg:(col-start-2 row-start-1)">
            <div tw="px-6 lg:px-12">
              <Heading tw="leading-tight my-0" level="h2">
                Partnering to Provide Better Care
              </Heading>
              <p tw="text-base md:text-xl my-4">
                Partnering with hospitals and community organizations to provide better care is a
                long-standing priority at VHA, but 2021 saw these collaborations expand and
                strengthen in new and exciting ways.
              </p>
              <Text tw="mt-6">
                <Button to="/better-care/">View Partnering to Provide Better Care</Button>
              </Text>
              <div tw="flex items-center space-x-6 mb-12 mt-12">
                <div tw="flex items-center space-x-4 justify-end">
                  <p>
                    <span tw="block text-2xl md:text-4xl font-bold">17</span>
                    <span tw="block text-base md:text-xl">
                    Ontario Health Teams in which VHA is an active partner across the regions where we provide service
                    </span>
                  </p>
                  <SpeechBubblePlusCircle tw="w-20 h-20 md:(w-24 h-24) flex-shrink-0 lg:(w-48 h-48)" />
                </div>
              </div>
            </div>
          </div>
          <div tw="lg:(col-start-1 row-start-1)">
            <StaticImage src="../images/desmond-hero.png" alt="" />
          </div>
        </div>
      </div>
    </FullscreenVideo>
    <FullscreenVideo
      src={{
        webm: supporintgOurTeamWebm,
        mp4: supporintgOurTeamMp4
      }}
    >
      <div tw="bg-[#000080] text-white bg-opacity-95 pt-12">
        <div tw="grid lg:(grid-cols-2 gap-12 items-end)">
          <div tw="flex flex-col items-center justify-center">
            <div tw="px-6 lg:px-12">
              <Heading tw="leading-tight my-0" level="h2">
                Supporting Our Team
              </Heading>
              <p tw="text-base md:text-xl my-4">
                2021 was a groundbreaking year for Diversity, Equity and Inclusion (DEI) initiatives
                at VHA. This work revolves around the drive to ensure all team members feel
                empowered, valued and are treated with respect.
              </p>
              <Text tw="mt-6">
                <Button to="/supporting-our-team/">View Supporting Our Team</Button>
              </Text>
              <div tw="flex items-center space-x-6 mb-12 mt-12">
                <div tw="flex items-center space-x-4">
                  <FamilyScaleCircle tw="w-20 h-20 md:(w-24 h-24) flex-shrink-0 lg:(w-48 h-48)" />
                  <p>
                    <span tw="block text-2xl md:text-4xl font-bold">98%</span>
                    <span tw="block text-base md:text-xl">
                      of VHA staff and service providers would recommend VHA to friends & family who
                      require care.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <StaticImage src="../images/adam-hero.png" alt="" />
          </div>
        </div>
      </div>
    </FullscreenVideo>
    <FullscreenVideo
      src={{
        webm: homeCareScienceWebm,
        mp4: homeCareScienceMp4
      }}
    >
      <div tw="bg-[#480239] text-white bg-opacity-95 pt-12 lg:text-right">
        <div tw="grid lg:(grid-cols-2 gap-6 lg:gap-12 xl:gap-64 items-center) xl:grid-cols-2">
          <div tw="flex flex-col items-center justify-center lg:(col-start-2 row-start-1) xl:(col-start-2 row-start-1 col-span-2)">
            <div tw="px-6 lg:px-12">
              <Heading tw="leading-tight my-0" level="h2">
                Investing in Home Care Science
              </Heading>
              <p tw="text-base md:text-xl my-4">
                VHA is committed to advancing home care science and creating home care scientists.
              </p>
              <Text tw="mt-6">
                <Button to="/home-care-science/">View Investing in Home Care Science</Button>
              </Text>
              <div tw="flex items-center space-x-6 mt-12 lg:justify-end">
                <div tw="flex items-center space-x-4 justify-end">
                  <p>
                    <span tw="block text-2xl md:text-4xl font-bold">23</span>
                    <span tw="block text-base md:text-xl">
                      academic trainees
                    </span>
                  </p>
                  <PenPaperCircle tw="w-14 h-14 md:(w-20 h-20) flex-shrink-0 lg:(w-32 h-32)" />
                </div>
              </div>
            </div>
          </div>
          <div tw="lg:(col-start-1 row-start-1)">
            <StaticImage tw="xl:mt-12" src="../images/donabelle-hero.png" alt="" />
          </div>
        </div>
      </div>
    </FullscreenVideo>
    <FullscreenVideo
      src={{
        webm: safePlaceWebm,
        mp4: safePlaceMp4
      }}
    >
      <div tw="bg-gray-100 bg-opacity-95 pt-12">
        <div tw="grid lg:(items-end grid-cols-2 gap-12)">
          <div tw="flex flex-col items-center justify-center">
            <div tw="px-6 lg:px-12 mb-12">
              <Heading tw="leading-tight my-0" level="h2">
                Home: A Safe Place to Receive Care
              </Heading>
              <p tw="text-base md:text-xl my-4">
              Families with young children, particularly those whose children have complex medical needs, have faced significant uncertainty throughout the pandemic.
              </p>
              <Text>
                <Button tw="text-white bg-gray-700 hover:text-black" to="/safe-place/">View Home: A Safe Place to Receive Care</Button>
              </Text>
              <div tw="flex items-center space-x-6 mb-12 mt-12">
                <div tw="flex items-center space-x-4 justify-end">
                <MaskCircle tw="w-20 h-20 md:(w-24 h-24) flex-shrink-0 lg:(w-40 h-40)" />
                  <p>
                    <span tw="block text-2xl md:text-4xl font-bold">1,514</span>
                    <span tw="block text-base md:text-xl">
                    COVID-19 positive clients cared for by VHA staff and service providers
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <StaticImage src="../images/christina-hero.png" alt="" />
          </div>
        </div>
      </div>
    </FullscreenVideo>
    <div tw="pt-16 bg-[#121C48] text-white">
      <div tw="px-4 pb-12 lg:(pb-0)">
        <div tw="grid gap-6 lg:(grid-cols-2) lg:gap-0">
          <StaticImage tw="lg:mt-24" src="../images/hands-on-hips.png" alt="" />
          <div tw="text-right bg-none mr-6">
            <Heading level="h2">By the Numbers</Heading>
            <div tw="flex flex-col space-y-4 items-end">
              <div tw="flex items-center space-x-10">
                <p>
                  <span tw="block text-2xl md:text-4xl font-bold">3,533,776</span>
                  <span tw="block text-base md:text-xl">units of service delivered</span>
                </p>
                <HeartInHands tw="w-24 h-24 flex-shrink-0 lg:(w-32 h-32)" />
              </div>
              <div tw="flex items-center space-x-10">
                <p>
                  <span tw="block text-2xl md:text-4xl font-bold">85%</span>
                  <span tw="block text-base md:text-xl">
                    of VHA staff would recommend working at VHA to a friend
                  </span>
                </p>
                <TwoDoctors tw="w-24 h-24 flex-shrink-0 lg:(w-32 h-32)" />
              </div>
              <div tw="flex items-center space-x-10">
                <p>
                  <span tw="block text-2xl md:text-4xl font-bold">2,663</span>
                  <span tw="block text-base md:text-xl">staff &amp; service providers</span>
                </p>
                <ThreeDoctors tw="w-24 h-24 flex-shrink-0 lg:(w-32 h-32)" />
              </div>
              <div tw="flex items-center space-x-10">
                <p>
                  <span tw="block text-2xl md:text-4xl font-bold">76</span>
                  <span tw="block text-base md:text-xl">client partners</span>
                </p>
                <HoldingHandsShield tw="w-24 h-24 flex-shrink-0 lg:(w-32 h-32)" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
);

export default IndexPage;
